import type { FilterValue, ListQueryParams, ListResponse, SortDirection } from '@warehouse/shared';

import type { QueryValue } from './api-client';

/**
 * Client half of the ONE list-query contract (`listQueryParamsSchema` in
 * @warehouse/shared, implemented server-side in
 * apps/api/src/core/db/list-query.ts). Every list page in the app builds its
 * request through here so search/filter/sort/paging behave identically.
 *
 * WIRE FORMAT — structured filters go out in the standard `qs` bracket shape:
 *
 *     ?q=abc&sort=-createdAt&page=2&pageSize=50
 *      &filters[status]=open&filters[binIds][]=A&filters[binIds][]=B
 *      &filters[deletedAt]=__null__
 *
 * `null` is transported as the `__null__` sentinel rather than an empty string:
 * `filters[x]=` parses to `""`, which the shared `filterValueSchema` happily
 * accepts as a STRING, so "IS NULL" would silently become "= ''".
 *
 * ⚠ The api must run express with `query parser: extended` for the bracket
 * syntax to arrive as a nested object — express 5 defaults to `simple`, which
 * yields the literal key `"filters[status]"` and zod then strips it (a filter
 * that silently does nothing). Tracked in the handover notes; nothing on this
 * side changes when it is fixed.
 */

export const NULL_FILTER_SENTINEL = '__null__';

export interface ListState {
  q?: string | undefined;
  filters?: Record<string, FilterValue | undefined> | undefined;
  /** Field name; `-field` for descending (or use `dir`). */
  sort?: string | undefined;
  dir?: SortDirection | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
  /** Keyset pagination — mutually exclusive with `page`. */
  cursor?: string | undefined;
}

export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

/**
 * Flatten a `ListState` into the query record the fetch client serializes.
 * Empty strings, empty arrays and `undefined` are dropped so a cleared search
 * box produces the same URL as an untouched one (and therefore the same
 * TanStack Query cache key).
 */
export function listQueryParams(state: ListState): Record<string, QueryValue> {
  const out: Record<string, QueryValue> = {};

  const q = state.q?.trim();
  if (q) out.q = q;

  if (state.sort) out.sort = state.sort;
  if (state.dir) out.dir = state.dir;

  if (state.cursor) {
    out.cursor = state.cursor;
  } else if (state.page && state.page > 1) {
    out.page = state.page;
  }
  if (state.pageSize) out.pageSize = state.pageSize;

  for (const [key, value] of Object.entries(state.filters ?? {})) {
    if (value === undefined) continue;
    if (value === null) {
      out[`filters[${key}]`] = NULL_FILTER_SENTINEL;
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      out[`filters[${key}]`] = value;
      continue;
    }
    if (typeof value === 'string' && value.length === 0) continue;
    out[`filters[${key}]`] = value;
  }

  return out;
}

/** The params object as the shared zod schema sees it (used in tests/mocks). */
export function toListQueryParams(state: ListState): ListQueryParams {
  const filters = Object.fromEntries(
    Object.entries(state.filters ?? {}).filter(([, v]) => v !== undefined),
  ) as Record<string, FilterValue>;

  return {
    ...(state.q?.trim() ? { q: state.q.trim() } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    ...(state.sort ? { sort: state.sort } : {}),
    ...(state.dir ? { dir: state.dir } : {}),
    page: state.page ?? 1,
    pageSize: state.pageSize ?? DEFAULT_PAGE_SIZE,
    ...(state.cursor ? { cursor: state.cursor } : {}),
  };
}

/**
 * A stable, order-independent TanStack Query key fragment for a list state.
 * Object key order must not create a second cache entry for the same query.
 */
export function listQueryKey(state: ListState): unknown {
  const params = listQueryParams(state);
  return Object.fromEntries(Object.entries(params).sort(([a], [b]) => a.localeCompare(b)));
}

/** Toggle a column's sort: asc → desc → cleared. */
export function nextSort(
  current: { sort?: string | undefined; dir?: SortDirection | undefined },
  field: string,
): { sort: string | undefined; dir: SortDirection | undefined } {
  if (current.sort !== field) return { sort: field, dir: 'asc' };
  if (current.dir === 'asc') return { sort: field, dir: 'desc' };
  return { sort: undefined, dir: undefined };
}

/** Total page count for offset pagination (at least 1, so the footer renders). */
export function pageCount(total: number | null, pageSize: number): number {
  if (total === null || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

export function emptyListResponse<T>(pageSize = DEFAULT_PAGE_SIZE): ListResponse<T> {
  return { items: [], total: 0, page: 1, pageSize, nextCursor: null };
}
